/**
 * Notificacao (o "toast" do design system).
 *
 * O produto não tinha nenhuma: toda confirmação de ação era um `Banner` fixo no
 * meio da tela, que empurra o conteudo para baixo e continua la depois que já
 * foi lido. Aviso e o oposto disso: aparece no canto, não mexe no layout e sai
 * sozinho.
 *
 * POR QUE `role="status"` E NAO `role="alert"`
 *
 *   `alert` interrompe o leitor de tela no meio da frase. Serve para o que a
 *   pessoa precisa saber AGORA, e "Contato salvo" não e isso. O aviso de erro
 *   entra como `alert`, o resto como `status`, que espera a frase terminar.
 *
 * POR QUE O AVISO DE ERRO NAO SOME SOZINHO
 *
 *   Confirmacao pode sumir: se a pessoa perdeu, não perdeu nada, a ação deu
 *   certo. Falha não: sumir depois de 2,4 segundos e esconder que a coisa
 *   não aconteceu. O de erro fica até alguem fechar - é a exceção deliberada
 *   ao `Yard.toast` de componentes.html §04.18, que some sempre; o próprio
 *   sistema manda o que exige decisão para o alerta em linha.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Icone } from './icones';

/**
 * `info` é o neutro e o de processo do sistema: grafite com texto branco.
 * `atencao` (F1, 26/10/2026) fecha o vocabulário de estado com o `Banner`:
 * deu certo, mas com ressalva ("Salvo. O domínio ainda não propagou.").
 */
export type TomDeAviso = 'ok' | 'erro' | 'info' | 'atencao';

/**
 * `acao` (F1, rodada 3, a pedido da F6): um botão de texto dentro do aviso,
 * para "Desfazer" onde a remoção é barata e não pede confirmação. Clicar
 * roda a ação e fecha o aviso. O aviso com ação fica 6s em vez de 2,4s:
 * ninguém lê a frase, decide e alcança o botão em menos que isso.
 */
export type AcaoDoAviso = { rotulo: string; aoClicar: () => void };

type Aviso = { id: number; tom: TomDeAviso; texto: string; titulo?: string; acao?: AcaoDoAviso };

const Contexto = createContext<(a: Omit<Aviso, 'id'>) => void>(() => {});

/** `avisar({ tom: 'ok', texto: 'Contato salvo.' })` de qualquer tela. */
export const useAvisar = () => useContext(Contexto);

/* 2,4s, como o `Yard.toast` do sistema (componentes.html §04.18). */
const DURACAO = 2400;
const DURACAO_COM_ACAO = 6000;
/* No máximo três visíveis: as mais antigas saem da pilha primeiro. */
const TETO = 3;

export function AvisoProvider({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);

  const avisar = useCallback((a: Omit<Aviso, 'id'>) => {
    setAvisos((atuais) => [...atuais, { ...a, id: Date.now() + Math.random() }].slice(-TETO));
  }, []);

  const fechar = useCallback((id: number) => {
    setAvisos((atuais) => atuais.filter((a) => a.id !== id));
  }, []);

  const valor = useMemo(() => avisar, [avisar]);

  return (
    <Contexto.Provider value={valor}>
      {children}
      <Pilha avisos={avisos} aoFechar={fechar} />
    </Contexto.Provider>
  );
}

/** A pilha de avisos. Exportada solta para a galeria montar sem o provedor. */
export function Pilha({
  avisos,
  aoFechar,
  fixos = false,
}: {
  avisos: Aviso[];
  aoFechar: (id: number) => void;
  /** Sem o relógio de 2,4s: só para a galeria mostrar os três tons parados. */
  fixos?: boolean;
}) {
  if (avisos.length === 0) return null;
  return (
    <div className="avisos" aria-live="polite">
      {/* Na galeria (`fixos`) aparecem todos os tons; no produto, o teto. */}
      {(fixos ? avisos : avisos.slice(-TETO)).map((a) => (
        <Cartao key={a.id} aviso={a} aoFechar={() => aoFechar(a.id)} fixo={fixos} />
      ))}
    </div>
  );
}

const SAIDA_MS = 280;

/**
 * Aparecer/sumir de toast (movimento.html §3): entra com `.on` num quadro
 * depois de montar, sai com `.saindo` - e só desmonta de verdade quando a
 * transição de 280ms termina, senão o cartão pisca fora sem a animação de
 * saída acontecer.
 */
function Cartao({ aviso, aoFechar, fixo }: { aviso: Aviso; aoFechar: () => void; fixo: boolean }) {
  const [entrou, setEntrou] = useState(false);
  const [saindo, setSaindo] = useState(false);

  useEffect(() => {
    const quadro = requestAnimationFrame(() => setEntrou(true));
    return () => cancelAnimationFrame(quadro);
  }, []);

  const fecharComSaida = useCallback(() => {
    setSaindo(true);
    window.setTimeout(aoFechar, SAIDA_MS);
  }, [aoFechar]);

  useEffect(() => {
    if (aviso.tom === 'erro' || fixo) return;
    const relogio = window.setTimeout(fecharComSaida, aviso.acao ? DURACAO_COM_ACAO : DURACAO);
    return () => window.clearTimeout(relogio);
  }, [aviso.tom, aviso.acao, fixo, fecharComSaida]);

  return (
    <div
      className={`aviso aviso--${aviso.tom}${entrou ? ' on' : ''}${saindo ? ' saindo' : ''}`}
      role={aviso.tom === 'erro' ? 'alert' : 'status'}
    >
      {/* Igual ao `Yard.toast` de componentes.html 04.18: só a frase, numa
          pílula grafite, verde ou vermelha. Sem ícone e sem título em negrito;
          o título, quando vem, entra na mesma frase. O botão de fechar existe
          só no erro, que por decisão do projeto não some sozinho. */}
      <span className="aviso__texto">
        {aviso.titulo ? `${aviso.titulo} ${aviso.texto}` : aviso.texto}
      </span>
      {aviso.acao && (
        <button
          type="button"
          className="aviso__acao"
          onClick={() => { aviso.acao!.aoClicar(); fecharComSaida(); }}
        >
          {aviso.acao.rotulo}
        </button>
      )}
      {aviso.tom === 'erro' && (
        <button type="button" className="botao-icone" onClick={fecharComSaida}>
          <Icone.Fechar titulo="Fechar aviso" tamanho={16} />
        </button>
      )}
    </div>
  );
}
